import { useMemo } from 'react';
import getColumnSizing, { fillLastColumn } from '../getColumnSizing';

import * as _ from 'lodash';

import ApplyFilter from '../components/ApplyFilter';

import {
  GET_FAVORITE_PRODUCT_DETAIL_CUSTOMIZED_GRID,
  GET_PRODUCT_DETAIL_CUSTOMIZED_GRID,
} from 'services/product/endpoints';

const URL_LIST_TO_DISABLE_SORT_IS_OWNER = [
  GET_PRODUCT_DETAIL_CUSTOMIZED_GRID,
  GET_FAVORITE_PRODUCT_DETAIL_CUSTOMIZED_GRID,
];

const tooltipFromField = (field) =>
  field ? ({ data }) => data?.[field] : undefined;

const useGetColumnDefs = ({
  urlGrid,
  columnDefs,
  notShowHeaderCheckbox,
  hideAgSetColumnFilter,
  defaultColDef,
}) => {
  const procColDef = useMemo(() => {
    if (!columnDefs) return [];
    let nextColDefs = [];

    columnDefs.forEach((colDef) => {
      if (colDef && colDef?.checkboxSelection) {
        // v32.2+ deprecates colDef.checkboxSelection; CheckboxSelectionRender draws the checkbox
        const { checkboxSelection, ...checkboxColDef } = colDef;

        nextColDefs.push({
          ...checkboxColDef,
          width: 60,
          field: 'checkboxSelection',
          filter: false,
          sortable: false,
          resizable: false,
          suppressAutoSize: true,
          suppressHeaderMenuButton: true,
          cellRenderer: 'CheckboxSelectionRender',

          headerComponent: !notShowHeaderCheckbox
            ? 'HeaderCheckboxRender'
            : 'HeaderNullRender',
          headerComponentParams: {
            urlGrid,
          },
        });
        return;
      }

      if (colDef && colDef.field === 'dragColumn') {
        nextColDefs.push({
          ...colDef,
          width: 50,
          field: 'dragColumn',
          filter: false,
          sortable: false,
          resizable: false,
          suppressAutoSize: true,
          suppressHeaderMenuButton: true,
          cellRenderer: 'DragRender',
          headerComponent: () => null,
        });
        return;
      }

      nextColDefs.push({
        colId: colDef?.colId,
        headerName: colDef?.headerName ?? colDef?.displayName,
        headerValueGetter: colDef?.headerValueGetter,
        headerTooltip:
          colDef?.headerTooltip ?? colDef?.headerName ?? colDef?.displayName,
        suppressHeaderFilterButton: colDef?.suppressHeaderFilterButton,
        headerClass: colDef && colDef.headerClass,
        field: (colDef && colDef.field) || colDef.fieldNameCamelCase,
        width: colDef && colDef.width,
        minWidth: colDef && colDef.minWidth,
        maxWidth: colDef && colDef.maxWidth,
        flex: colDef && colDef.flex,
        filter: colDef?.filterFramework ?? getFilter(colDef),
        filterParams: getFilterParams({
          data: colDef,
          hideAgSetColumnFilter,
          initialFilter: defaultColDef?.initialFilter,
        }),
        hide: colDef && colDef.hide,
        pinned: colDef && colDef.pinned,
        lockPosition: colDef && colDef.lockPosition,
        lockVisible: colDef && colDef.lockVisible,
        lockPinned: colDef && colDef.lockPinned,
        sortable: getSortable(colDef),
        sort: colDef && colDef.sort,
        sortIndex: null,
        resizable: colDef && colDef.resizable,
        cellRenderer:
          colDef && colDef.dataType !== 'boolean'
            ? colDef.linkTo
              ? 'LinkRender'
              : colDef.cellRenderer
            : 'CheckboxRender',

        valueFormatter: colDef && colDef.valueFormatter,
        suppressHeaderMenuButton:
          colDef?.suppressHeaderMenuButton ?? colDef?.suppressMenu,
        rowDrag: colDef && colDef.rowDrag,
        // app-only fields go in context: v36 validation rejects unknown colDef keys
        context: {
          orgFieldName: colDef?.orgFieldName,
          separateFieldName: colDef?.separateFieldName,
          displayName: colDef?.displayName,
          linkTo: colDef?.linkTo,
          linkTarget: colDef?.linkTarget,
        },
        valueGetter: colDef && colDef.valueGetter,
        cellStyle: colDef && colDef.cellStyle,
        headerComponent: colDef?.headerComponent,
        headerComponentParams: colDef?.headerComponentParams,
        suppressAutoSize:
          colDef?.suppressAutoSize ??
          (colDef?.resizable === false ||
            (colDef?.headerName ?? colDef?.displayName) === ''),
        suppressSizeToFit: colDef?.suppressSizeToFit,
        tooltip: colDef?.tooltip ?? tooltipFromField(colDef?.tooltipField),
      });
    });

    nextColDefs = nextColDefs.map((column) => {
      const colItem = { ...column, ...getColumnSizing(column) };
      if (URL_LIST_TO_DISABLE_SORT_IS_OWNER.includes(urlGrid)) {
        if (colItem?.field === 'isOwner') {
          return { ...colItem, sortable: false };
        }
      }

      return { ...colItem };
    });

    return fillLastColumn(nextColDefs);
  }, [columnDefs, hideAgSetColumnFilter, notShowHeaderCheckbox, urlGrid]);

  return procColDef;
};

const getFilter = (colDef) => {
  const disabledFilterType = ['subscriptionshieldtype'];

  if (!colDef) return false;

  if (!colDef?.allowFilter) return false;

  if (colDef?.filter) return colDef.filter;

  if (colDef.isShowFilter === 'none') return false;

  if (disabledFilterType.indexOf(colDef.dataType) > -1)
    return false;

  return 'agMultiColumnFilter';
};

const getSortable = (colDef) => {
  const disabledSortableType = ['boolean', 'subscriptionshieldtype'];

  if (!colDef) return false;

  if (
    disabledSortableType.indexOf(colDef?.dataType) > -1 &&
    !colDef?.allowBooleanSort
  )
    return false;

  return colDef?.allowSort;
};

const filterParamsButton = {
  buttons: ['apply', 'reset'],
  closeOnApply: true,
};

const getFilterParams = ({ data, hideAgSetColumnFilter, initialFilter }) => {
  let textFilter =
    data.dataType === 'string'
      ? 'agTextColumnFilter'
      : data.dataType === 'int' ||
          data.dataType === 'decimal' ||
          data.dataType === 'int32' ||
          data.dataType === 'int64'
        ? 'agNumberColumnFilter'
        : data.dataType === 'datetime'
          ? 'agDateColumnFilter'
          : true;
  let filter = !data.allowFilter ? false : textFilter;
  filter = data?.filter === 'agSetColumnFilter' ? data?.filter : filter;

  let filterParams = {};

  if (filter === 'agTextColumnFilter') {
    let isHideAgSetColumnFilter =
      typeof hideAgSetColumnFilter === 'string'
        ? data.fieldName === hideAgSetColumnFilter
        : _.isArray(hideAgSetColumnFilter)
          ? hideAgSetColumnFilter.includes(data.fieldName)
          : false;

    if (isHideAgSetColumnFilter) {
      filterParams = {
        filters: [
          {
            filter: 'agTextColumnFilter',
            filterParams: {
              filterOptions: [
                'contains',
                'equals',
                'notEqual',
                'blank',
                'notBlank',
              ],
              maxNumConditions: 1,
              ...filterParamsButton,
            },
          },
          {
            filter: ApplyFilter,
          },
        ],
      };
    } else {
      filterParams = {
        filters: [
          {
            filter: 'agTextColumnFilter',
            filterParams: {
              filterOptions: [
                'contains',
                'equals',
                'notEqual',
                'blank',
                'notBlank',
              ],
              maxNumConditions: 1,
              ...filterParamsButton,
            },
          },
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              ...filterParamsButton,
              suppressSorting: true,
              values: getInitialSetFilterValues({
                data,
                initialFilter,
              }),
            },
          },
          {
            filter: ApplyFilter,
          },
        ],
      };
    }
  }
  if (filter === 'agNumberColumnFilter') {
    filterParams = {
      filters: [
        {
          filter: 'agNumberColumnFilter',
          filterParams: {
            filterOptions: [
              'equals',
              'notEqual',
              'lessThan',
              'lessThanOrEqual',
              'greaterThan',
              'greaterThanOrEqual',
            ],
            ...filterParamsButton,
            maxNumConditions: 1,
          },
        },
        {
          filter: 'agSetColumnFilter',
          filterParams: {
            ...filterParamsButton,
            suppressSorting: true,
            values: getInitialSetFilterValues({
              data,
              initialFilter,
            }),
          },
        },
        {
          filter: ApplyFilter,
        },
      ],
    };
  }
  if (filter === 'agDateColumnFilter') {
    filterParams = {
      filters: [
        {
          filter: 'agDateColumnFilter',
          filterParams: {
            filterOptions: [
              'equals',
              'lessThan',
              'lessThanOrEqual',
              'greaterThan',
              'greaterThanOrEqual',
            ],
            ...filterParamsButton,
            maxNumConditions: 1,
            minValidYear: 0,
            maxValidYear: 9999,
          },
        },
        {
          filter: ApplyFilter,
        },
        // {
        //   filter: 'agSetColumnFilter',
        // },
      ],
    };
  }

  if (filter === 'agSetColumnFilter') {
    filterParams = {
      ..._.cloneDeep(data.filterParams),
      ...filterParamsButton,
      values: getInitialSetFilterValues({ data, initialFilter }),
    };
  }

  if (data.dataType === 'boolean') {
    // null/missing come back from distinct as "N/A" - must not collapse into False
    const formatBooleanValue = (value) => {
      const normalizedValue = value?.toString()?.toLowerCase();
      if (normalizedValue === 'true') return 'True';
      if (normalizedValue === 'false') return 'False';
      return 'N/A';
    };
    const booleanFilterOrder = ['False', 'True', 'N/A'];
    const compareBooleanValues = (a, b) =>
      booleanFilterOrder.indexOf(formatBooleanValue(a)) -
      booleanFilterOrder.indexOf(formatBooleanValue(b));

    filterParams = {
      filters: [
        {
          filter: 'agSetColumnFilter',
          filterParams: {
            valueFormatter: (params) => formatBooleanValue(params.value),
            comparator: compareBooleanValues,
            ...filterParamsButton,
            values: ['True', 'False'],
            // values: getInitialSetFilterValues({
            //   data,
            //   initialFilter,
            // }),
          },
        },
        {
          filter: ApplyFilter,
        },
      ],
    };
  }

  return filterParams;
};

const getInitialSetFilterValues = ({ data, initialFilter }) => {
  if (!data?.field && !data?.fieldNameCamelCase) return [];

  const field = data?.field ?? data?.fieldNameCamelCase;
  const filterModel = initialFilter?.[field];
  if (!filterModel) return [];

  const filterType = filterModel?.filterType;

  if (filterType === 'multi') {
    const setFilter = filterModel?.filterModels.find(
      (filterItem) => filterItem?.filterType === 'set'
    );

    if (!setFilter) return [];

    if (!setFilter?.values?.length) return [];
    return [
      ...setFilter?.values,
      '  ', //* Dummy value to prevent AG Grid from triggering "Select All" logic.
    ];
  }

  if (filterType === 'set') {
    return [
      ...filterModel?.values,
      '  ', //* Dummy value to prevent AG Grid from triggering "Select All" logic.
    ];
  }

  return [];
};

export { useGetColumnDefs };
